'use client';

import type { FC, ReactNode } from 'react';
import { SkinImage } from '../../components/ui/SkinImage';
import { abbreviateWearCondition, formatCreditsNumber, formatCreditsShort, parseItemDetails } from '../../utils/formatters';

interface PairItem {
  name: string;
  imageUrl: string | null;
}

interface UpgradePairLineProps {
  source: PairItem;
  sourceAmount: number | null;
  target: PairItem;
  footer?: ReactNode;
}

const Thumb: FC<{ item: PairItem }> = ({ item }) => (
  <span className="relative block h-11 w-14 shrink-0 overflow-hidden rounded-lg bg-white/[0.06]">
    <SkinImage name={item.name} src={item.imageUrl} imgClassName="h-full w-full object-contain" />
  </span>
);

export const UpgradePairLine: FC<UpgradePairLineProps> = ({ source, sourceAmount, target, footer = null }) => {
  const { weaponName, skinName, condition, isStar, isStatTrak, isSouvenir } = parseItemDetails(target.name);
  const quality = [isStar && '★', isStatTrak && 'StatTrak™', isSouvenir && 'Souvenir'].filter(Boolean).join(' ');

  return (
    <span className="flex w-full min-w-0 items-center gap-2">
      <span className="relative shrink-0">
        <Thumb item={source} />
        {!!sourceAmount && (
          <span className="absolute inset-x-[-16px] -bottom-1.5 flex justify-center">
            <span
              dir="ltr"
              title={`+${formatCreditsNumber(sourceAmount)}`}
              className="whitespace-nowrap rounded-full bg-bg-secondary px-1 text-[10px] font-semibold leading-[14px] tabular-nums text-price"
            >
              +{formatCreditsShort(sourceAmount)}
            </span>
          </span>
        )}
      </span>
      <span aria-hidden className="inline-block shrink-0 text-white/30 rtl:-scale-x-100">→</span>
      <Thumb item={target} />
      <span className="flex min-w-0 flex-1 flex-col ps-1">
        <span dir="ltr" className="max-w-full self-start truncate text-[11px] leading-[14px] text-white/50">
          {quality && <span className="text-price">{quality} </span>}
          {weaponName}
          {condition && ` · ${abbreviateWearCondition(condition)}`}
        </span>
        <span className="line-clamp-2 break-words text-[15px] font-semibold leading-[18px] text-white">{skinName || target.name}</span>
        {footer}
      </span>
    </span>
  );
};
